import { $FixMe } from '@eventbrite/ts-utils';
import React, { FunctionComponent, useEffect, useRef } from 'react';
import { loadReCaptcha, ReCaptcha } from 'react-recaptcha-v3';
import { CaptchaStatus } from '../../models/app';
import { CaptchaSettings } from '../../models/login';

export const CAPTCHA_RETRY_INTERVAL = 110000;
export const CAPTCHA_LOADING_TIMEOUT = 2000;

export interface ReCaptchaV3Props {
    captcha: CaptchaSettings;
    captchaStatus: CaptchaStatus;
    enabledReCaptchaV3: boolean;
    setCaptchaV3Status: (status: CaptchaStatus) => void;
    setCaptchaV3Token: (token: string) => void;
    setTimeoutReached: (isReached: boolean) => void;
}

const clearCaptchaTimeout = (
    timeout: React.MutableRefObject<NodeJS.Timeout | null>,
) => {
    if (timeout.current) {
        clearTimeout(timeout.current);
        timeout.current = null;
    }
};

export const ReCaptchaV3: FunctionComponent<ReCaptchaV3Props> = ({
    captcha,
    captchaStatus,
    enabledReCaptchaV3,
    setCaptchaV3Status,
    setCaptchaV3Token,
    setTimeoutReached,
}) => {
    const handleCaptchaV3Timeout = useRef<NodeJS.Timeout | null>(null);
    const initialLoadCaptchaV3TokenTimeout = useRef<NodeJS.Timeout | null>(
        null,
    );
    let recaptchaRef: $FixMe;

    const updateToken = () => {
        recaptchaRef?.execute();
    };

    const handleCaptchaV3 = (captchaToken: string) => {
        setCaptchaV3Status(CaptchaStatus.loaded);
        setCaptchaV3Token(captchaToken);

        // The recaptcha token expires after two minutes and we need to refresh it
        handleCaptchaV3Timeout.current = setTimeout(() => {
            updateToken();
        }, CAPTCHA_RETRY_INTERVAL);
    };

    useEffect(() => {
        if (captchaStatus === CaptchaStatus.refreshing) {
            setCaptchaV3Status(CaptchaStatus.loaded);
            recaptchaRef?.execute();
        }
    }, [captchaStatus, setCaptchaV3Status, recaptchaRef]);

    useEffect(() => {
        if (
            captcha?.recaptcha3PublicKey &&
            enabledReCaptchaV3 &&
            captchaStatus != CaptchaStatus.loaded
        ) {
            setCaptchaV3Status(CaptchaStatus.loading);

            loadReCaptcha(captcha.recaptcha3PublicKey);

            initialLoadCaptchaV3TokenTimeout.current = setTimeout(() => {
                setTimeoutReached(true);
            }, CAPTCHA_LOADING_TIMEOUT);
        }

        return () => {
            clearCaptchaTimeout(initialLoadCaptchaV3TokenTimeout);
            clearCaptchaTimeout(handleCaptchaV3Timeout);
        };
    }, [
        captcha,
        captchaStatus,
        enabledReCaptchaV3,
        setCaptchaV3Status,
        setTimeoutReached,
    ]);

    const reCaptchaComponent =
        enabledReCaptchaV3 && captcha?.recaptcha3PublicKey ? (
            <ReCaptcha
                ref={(ref: $FixMe) => (recaptchaRef = ref)}
                sitekey={captcha.recaptcha3PublicKey}
                action="checkout"
                verifyCallback={handleCaptchaV3}
            />
        ) : null;

    return reCaptchaComponent;
};
